/**
 * Normiz Compliance Intelligence Engine
 * React Hooks for Compliance API
 *
 * These hooks provide a React Query interface to the Firebase Cloud Functions
 * that power the compliance checking system.
 */

import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { parseAddress, validateAddressForLookup } from '@/utils/addressParser';
import type {
  JurisdictionFeeSchedule,
  ProcessingTimeline,
  InspectionRequirements,
  DepartmentContacts,
  NoticeOfCommencement,
  TradeSpecificForms,
  OwnerBuilderRules,
  PermitExemption,
  PhysicalAddress,
  OfficeHours,
  OnlinePortal,
  PermittingProcess,
  ContractorWarnings,
  CommercialRequirements,
} from '@/types/jurisdiction';

// ============================================
// ERROR HANDLING UTILITIES
// ============================================

/**
 * Compliance-specific error codes for better error handling
 */
export type ComplianceErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'UNAUTHENTICATED'
  | 'PERMISSION_DENIED'
  | 'NOT_FOUND'
  | 'INVALID_INPUT'
  | 'SERVER_ERROR'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

export interface ComplianceError {
  code: ComplianceErrorCode;
  message: string;
  retryable: boolean;
  details?: string;
}

/**
 * Parse Firebase/network errors into user-friendly messages
 */
function parseComplianceError(error: any): ComplianceError {
  const errorMessage = error?.message || error?.toString() || 'Unknown error';
  const errorCode = error?.code || '';

  // Network errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorCode === 'unavailable') {
    return {
      code: 'NETWORK_ERROR',
      message: 'Network connection failed. Please check your internet connection.',
      retryable: true,
      details: errorMessage,
    };
  }

  // Timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('deadline') || errorCode === 'deadline-exceeded') {
    return {
      code: 'TIMEOUT',
      message: 'The compliance check is taking longer than expected. Please try again.',
      retryable: true,
      details: errorMessage,
    };
  }

  // Authentication errors
  if (errorCode === 'unauthenticated' || errorMessage.includes('unauthenticated')) {
    return {
      code: 'UNAUTHENTICATED',
      message: 'Your session has expired. Please sign in again.',
      retryable: false,
      details: errorMessage,
    };
  }

  // Permission errors
  if (errorCode === 'permission-denied' || errorMessage.includes('permission')) {
    return {
      code: 'PERMISSION_DENIED',
      message: 'You do not have permission to perform this action.',
      retryable: false,
      details: errorMessage,
    };
  }

  // Not found errors
  if (errorCode === 'not-found' || errorMessage.includes('not found')) {
    return {
      code: 'NOT_FOUND',
      message: 'The requested resource was not found.',
      retryable: false,
      details: errorMessage,
    };
  }

  // Invalid input
  if (errorCode === 'invalid-argument' || errorMessage.includes('invalid')) {
    return {
      code: 'INVALID_INPUT',
      message: 'Invalid input provided. Please check your data.',
      retryable: false,
      details: errorMessage,
    };
  }

  // Rate limiting
  if (errorCode === 'resource-exhausted' || errorMessage.includes('rate') || errorMessage.includes('quota')) {
    return {
      code: 'RATE_LIMITED',
      message: 'Too many requests. Please wait a moment and try again.',
      retryable: true,
      details: errorMessage,
    };
  }

  // Server errors
  if (errorCode === 'internal' || errorCode === 'unknown' || errorMessage.includes('server')) {
    return {
      code: 'SERVER_ERROR',
      message: 'A server error occurred. Our team has been notified.',
      retryable: true,
      details: errorMessage,
    };
  }

  // Unknown errors
  return {
    code: 'UNKNOWN',
    message: errorMessage || 'An unexpected error occurred.',
    retryable: true,
    details: errorMessage,
  };
}

/**
 * Calculate retry delay with exponential backoff and jitter
 */
function calculateRetryDelay(attemptIndex: number): number {
  const baseDelay = 1000; // 1 second
  const maxDelay = 10000; // 10 seconds
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attemptIndex), maxDelay);
  // Add jitter (0-25% of delay)
  const jitter = exponentialDelay * Math.random() * 0.25;
  return exponentialDelay + jitter;
}

// ============================================
// TYPES
// ============================================

export interface JurisdictionOverlays {
  floodZone: boolean;
  coastalZone: boolean;
  historicDistrict: boolean;
  windBorneDebrisRegion: boolean;
  highVelocityHurricaneZone: boolean;
}

export interface Jurisdiction {
  id: number;
  code: string;
  ahjName: string;
  ahjShortCode: string;
  scopeLevel: string;
  phone: string;
  email: string;
  website: string;
  portalUrl: string;
  address?: string | null;
  overlays: JurisdictionOverlays;
  verificationStatus: string;
  dataSource: string;
  // Extended JSONB fields (from migrations 039, 040, 043)
  feeSchedule?: JurisdictionFeeSchedule | null;
  processingTimelines?: ProcessingTimeline | null;
  inspectionRequirements?: InspectionRequirements | null;
  departmentContacts?: DepartmentContacts | null;
  noticeOfCommencement?: NoticeOfCommencement | null;
  tradeSpecificForms?: TradeSpecificForms | null;
  ownerBuilderRules?: OwnerBuilderRules | null;
  permitExemptions?: PermitExemption[] | null;
  physicalAddress?: PhysicalAddress | null;
  officeHours?: OfficeHours | null;
  onlinePortal?: OnlinePortal | null;
  permittingProcess?: PermittingProcess | null;
  contractorWarnings?: ContractorWarnings | null;
  commercialRequirements?: CommercialRequirements | null;
}

export interface UsefulLinks {
  permitForms: string | null;
  zoningInfo: string | null;
  businessLicense: string | null;
  floodMaps: string;
  inspectionScheduling?: string | null;
  productApprovals?: string | null;
}

export interface JurisdictionLookupResult {
  success: boolean;
  jurisdiction?: Jurisdiction;
  usefulLinks?: UsefulLinks;
  warnings?: string[];
  resolvedFrom?: {
    state: string;
    county: string | null;
    city: string | null;
  };
  fallbackWarning?: string;
  fallbackLevel?: string;
  error?: string;
  message?: string;
}

/**
 * Matching custom form from user's library
 */
export interface MatchingCustomForm {
  id: string;
  name: string;
  fieldCount: number;
}

export interface ChecklistItem {
  id: number;
  name: string;
  description: string;
  category: string;
  categoryName: string;
  isRequired: boolean;
  severity: 'critical' | 'warning' | 'info';
  templateUrl?: string;
  instructions?: string;
  commonMistakes?: string;
  mustHaveEngineerStamp?: boolean;
  mustBeSigned?: boolean;
  maxAgeDays?: number;
  sourceSection?: string;
  sourceUrl?: string;
  validationRules: ValidationRule[];
  acceptedFormats: string[];
  // Fillable vs Upload distinction
  isFillable: boolean;
  uploadHelpText?: string | null;
  matchingCustomForms: MatchingCustomForm[];
  hasMatchingForm: boolean;
}

export interface ValidationRule {
  id: number;
  rule_code: string;
  rule_name: string;
  severity: string;
  validation_type: string;
  parameters: Record<string, any>;
}

export interface ChecklistResult {
  success: boolean;
  jurisdiction: {
    id: number;
    code: string;
    ahjName: string;
    isHvhz: boolean;
    // Extended JSONB fields for checklist context
    processingTimelines?: ProcessingTimeline | null;
    tradeSpecificForms?: TradeSpecificForms | null;
    departmentContacts?: DepartmentContacts | null;
    noticeOfCommencement?: NoticeOfCommencement | null;
    permitExemptions?: PermitExemption[] | null;
    inspectionRequirements?: InspectionRequirements | null;
    ownerBuilderRules?: OwnerBuilderRules | null;
    permittingProcess?: PermittingProcess | null;
    contractorWarnings?: ContractorWarnings | null;
    commercialRequirements?: CommercialRequirements | null;
  };
  permitType: {
    code: string;
    name: string;
  };
  jobScope?: {
    code: string;
    name: string;
  };
  checklist: ChecklistItem[];
  totalRequired: number;
  totalOptional: number;
}

/**
 * Code citation attached to a compliance finding
 * Contains metadata only - no copyrighted text
 */
export interface Citation {
  label: string;              // Short display label: 'FBC 105.1'
  fullReference?: string;     // Full formal reference
  codeType: 'building' | 'electrical' | 'plumbing' | 'mechanical' | 'energy' | 'fire' | 'statute' | 'administrative';
  editionYear?: number;       // Code edition year: 2023
  sectionNumber?: string;     // Section reference: '105.1'
  sourceUrl?: string;         // Link to official free source
  summary?: string;           // Brief description (NOT copyrighted text)
}

export interface Finding {
  ruleCode: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  affectedField?: string;
  expectedValue?: string;
  actualValue?: string;
  suggestedFix?: string;
  documentId?: string;
  status: string;
  citations?: Citation[];     // Code references for this finding
}

export interface ComplianceCheckResult {
  success: boolean;
  checkRunId?: string;
  status: 'pass' | 'fail' | 'needs_review';
  score: number;
  summary: {
    critical: number;
    warning: number;
    info: number;
    total: number;
  };
  findings: Finding[];
  jurisdiction?: {
    code: string;
    ahjName: string;
    isHvhz: boolean;
    // Extended JSONB fields for compliance context
    noticeOfCommencement?: NoticeOfCommencement | null;
    processingTimelines?: ProcessingTimeline | null;
    departmentContacts?: DepartmentContacts | null;
    ownerBuilderRules?: OwnerBuilderRules | null;
    permittingProcess?: PermittingProcess | null;
    commercialRequirements?: CommercialRequirements | null;
  };
  runDurationMs: number;
  error?: string;
  message?: string;
}

export interface County {
  id: number;
  name: string;
  fips_code: string;
}

export interface PermitType {
  id: number;
  code: string;
  name: string;
  description: string;
  sort_order: number;
}

export interface JobScope {
  id: number;
  code: string;
  name: string;
  description: string;
  trade_type_id: number;
  sort_order: number;
}

export type DocumentType =
  | 'contractor_license'
  | 'certificate_of_insurance'
  | 'manual_j_calculation'
  | 'equipment_spec_sheet'
  | 'noa_product_approval'
  | 'permit_application'
  | 'engineer_letter';

export interface ExtractedField {
  name: string;
  value: string | null;
  confidence: number;
}

export interface DocumentExtractionResult {
  success: boolean;
  documentId: string;
  documentType: DocumentType;
  extractedFields: ExtractedField[];
  rawText?: string;
  confidence: number;
  processingTimeMs: number;
  extractionMethod: 'gpt4_vision' | 'regex_fallback';
  error?: string;
  message?: string;
}

// ============================================
// HOOKS
// ============================================

/**
 * Look up jurisdiction by address components
 * Returns the AHJ (Authority Having Jurisdiction) for a location
 *
 * Features:
 * - Automatic retry with exponential backoff (3 attempts)
 * - Detailed error parsing with user-friendly messages
 */
export function useLookupJurisdiction() {
  const [lastError, setLastError] = useState<ComplianceError | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: {
      state: string;
      county?: string;
      city?: string;
      address?: string;
      zip?: string;
    }): Promise<JurisdictionLookupResult> => {
      setLastError(null);

      const lookupJurisdiction = httpsCallable<typeof params, JurisdictionLookupResult>(
        functions,
        'lookupJurisdiction',
        { timeout: 30000 } // 30 second timeout
      );
      const result = await lookupJurisdiction(params);
      return result.data;
    },
    onError: (error: any) => {
      const parsedError = parseComplianceError(error);
      setLastError(parsedError);
    },
    retry: (failureCount, error: any) => {
      if (failureCount >= 3) return false;
      const parsedError = parseComplianceError(error);
      return parsedError.retryable;
    },
    retryDelay: calculateRetryDelay,
  });

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    ...mutation,
    lastError,
    clearError,
  };
}

/**
 * Look up jurisdiction by a full address string
 * Parses the address and calls the jurisdiction lookup API
 *
 * Features:
 * - Address parsing and validation
 * - Automatic retry with exponential backoff (3 attempts)
 * - Detailed error parsing with user-friendly messages
 */
export function useJurisdictionLookupByAddress() {
  const [lastError, setLastError] = useState<ComplianceError | null>(null);

  const mutation = useMutation({
    mutationFn: async (fullAddress: string): Promise<JurisdictionLookupResult> => {
      setLastError(null);

      // Parse the address string
      const parsed = parseAddress(fullAddress);
      const validation = validateAddressForLookup(parsed);

      if (!validation.valid) {
        return {
          success: false,
          error: 'Invalid address',
          message: validation.errors.join('. '),
        };
      }

      // Call the jurisdiction lookup API
      const lookupJurisdiction = httpsCallable<{
        state: string;
        county?: string;
        city?: string;
        address?: string;
        zip?: string;
      }, JurisdictionLookupResult>(functions, 'lookupJurisdiction', { timeout: 30000 });

      const result = await lookupJurisdiction({
        state: parsed.state!,
        county: parsed.county || undefined,
        city: parsed.city || undefined,
        address: parsed.street || undefined,
        zip: parsed.zipCode || undefined,
      });

      return result.data;
    },
    onError: (error: any) => {
      const parsedError = parseComplianceError(error);
      setLastError(parsedError);
    },
    retry: (failureCount, error: any) => {
      if (failureCount >= 3) return false;
      const parsedError = parseComplianceError(error);
      return parsedError.retryable;
    },
    retryDelay: calculateRetryDelay,
  });

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    ...mutation,
    lastError,
    clearError,
  };
}

/**
 * Get required checklist for a permit type in a jurisdiction
 * Use this to display what documents are needed for a permit
 */
export function useRequiredChecklist(params: {
  jurisdictionId: number | null;
  permitType: string;
  jobScope?: string;
  projectValue?: number;
  riskFlags?: Record<string, boolean>;
}) {
  return useQuery({
    queryKey: ['compliance', 'checklist', params.jurisdictionId, params.permitType, params.jobScope],
    queryFn: async (): Promise<ChecklistResult> => {
      const getRequiredChecklist = httpsCallable<typeof params, ChecklistResult>(
        functions,
        'getRequiredChecklist'
      );
      const result = await getRequiredChecklist(params);
      return result.data;
    },
    enabled: !!params.jurisdictionId && !!params.permitType,
    staleTime: 1000 * 60 * 10, // 10 minutes - requirements don't change often
  });
}

/**
 * Run a compliance check on a job
 * Evaluates all uploaded documents against requirements
 *
 * Features:
 * - Automatic retry with exponential backoff (3 attempts)
 * - Detailed error parsing with user-friendly messages
 * - Persistent error state for UI display
 * - Query invalidation on success
 */
export function useRunComplianceCheck() {
  const queryClient = useQueryClient();
  const [lastError, setLastError] = useState<ComplianceError | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: {
      jobId: string;
      contractorId: string;
      permitType?: string;
      jobScope?: string;
    }): Promise<ComplianceCheckResult> => {
      // Clear previous error when starting new request
      setLastError(null);

      const runComplianceCheck = httpsCallable<typeof params, ComplianceCheckResult>(
        functions,
        'runComplianceCheck',
        { timeout: 120000 } // 2 minute timeout
      );
      const result = await runComplianceCheck(params);
      return result.data;
    },
    onSuccess: (data, variables) => {
      // Clear any previous error
      setLastError(null);
      // Invalidate job queries to refresh compliance status
      queryClient.invalidateQueries({ queryKey: ['jobs', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['compliance', 'history', variables.jobId] });
    },
    onError: (error: any) => {
      // Parse and store error for persistent display
      const parsedError = parseComplianceError(error);
      setLastError(parsedError);
    },
    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry if we've already tried 3 times
      if (failureCount >= 3) return false;

      // Parse error to check if retryable
      const parsedError = parseComplianceError(error);
      return parsedError.retryable;
    },
    retryDelay: calculateRetryDelay,
  });

  // Clear error function for UI
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    ...mutation,
    lastError,
    clearError,
  };
}

/**
 * State type for dropdown
 */
export interface SupportedState {
  id: number;
  code: string;
  name: string;
}

/**
 * Get all supported states for dropdowns
 */
export function useSupportedStates() {
  return useQuery({
    queryKey: ['compliance', 'states'],
    queryFn: async (): Promise<SupportedState[]> => {
      const getSupportedStates = httpsCallable<void, { success: boolean; states: SupportedState[] }>(
        functions,
        'getSupportedStates'
      );
      const result = await getSupportedStates();
      return result.data.states;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - states don't change
  });
}

/**
 * Get counties for a specific state
 */
export function useCountiesByState(stateCode: string | null) {
  return useQuery({
    queryKey: ['compliance', 'counties', stateCode],
    queryFn: async (): Promise<County[]> => {
      if (!stateCode) return [];
      const getCountiesByState = httpsCallable<{ stateCode: string }, { success: boolean; counties: County[] }>(
        functions,
        'getCountiesByState'
      );
      const result = await getCountiesByState({ stateCode });
      return result.data.counties;
    },
    enabled: !!stateCode,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - counties don't change
  });
}

/**
 * Get all Florida counties for dropdowns (DEPRECATED - use useCountiesByState)
 */
export function useFloridaCounties() {
  return useQuery({
    queryKey: ['compliance', 'counties', 'FL'],
    queryFn: async (): Promise<County[]> => {
      const getFloridaCounties = httpsCallable<void, { success: boolean; counties: County[] }>(
        functions,
        'getFloridaCounties'
      );
      const result = await getFloridaCounties();
      return result.data.counties;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - counties don't change
  });
}

/**
 * Get all permit types
 */
export function usePermitTypes() {
  return useQuery({
    queryKey: ['compliance', 'permitTypes'],
    queryFn: async (): Promise<PermitType[]> => {
      const getPermitTypes = httpsCallable<void, { success: boolean; permitTypes: PermitType[] }>(
        functions,
        'getPermitTypes'
      );
      const result = await getPermitTypes();
      return result.data.permitTypes;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Get job scopes for a trade type
 */
export function useJobScopes(tradeType?: string) {
  return useQuery({
    queryKey: ['compliance', 'jobScopes', tradeType],
    queryFn: async (): Promise<JobScope[]> => {
      const getJobScopes = httpsCallable<{ tradeType?: string }, { success: boolean; jobScopes: JobScope[] }>(
        functions,
        'getJobScopes'
      );
      const result = await getJobScopes({ tradeType });
      return result.data.jobScopes;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Extract structured data from a document using OCR and AI
 * Supports contractor licenses, COI, Manual J, equipment specs, NOA, permits, engineer letters
 *
 * Features:
 * - Automatic retry with exponential backoff (3 attempts)
 * - Detailed error parsing with user-friendly messages
 * - Persistent error state for UI display
 * - 5 minute timeout for large documents
 */
export function useExtractDocumentData() {
  const queryClient = useQueryClient();
  const [lastError, setLastError] = useState<ComplianceError | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: {
      documentId: string;
      documentType: DocumentType;
      storageUrl: string;
    }): Promise<DocumentExtractionResult> => {
      // Clear previous error when starting new request
      setLastError(null);

      const extractDocumentData = httpsCallable<typeof params, DocumentExtractionResult>(
        functions,
        'extractDocumentData',
        { timeout: 300000 } // 5 minute timeout for large documents
      );
      const result = await extractDocumentData(params);
      return result.data;
    },
    onSuccess: (_, variables) => {
      // Clear any previous error
      setLastError(null);
      // Invalidate document queries to refresh with extracted data
      queryClient.invalidateQueries({ queryKey: ['documents', variables.documentId] });
    },
    onError: (error: any) => {
      // Parse and store error for persistent display
      const parsedError = parseComplianceError(error);
      setLastError(parsedError);
    },
    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry if we've already tried 3 times
      if (failureCount >= 3) return false;

      // Parse error to check if retryable
      const parsedError = parseComplianceError(error);
      return parsedError.retryable;
    },
    retryDelay: calculateRetryDelay,
  });

  // Clear error function for UI
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    ...mutation,
    lastError,
    clearError,
  };
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook to get compliance status badge color and label
 */
export function useComplianceStatus(status?: 'pass' | 'fail' | 'needs_review' | null) {
  if (!status) {
    return { color: 'gray', label: 'Not Checked', icon: 'circle-help' };
  }

  switch (status) {
    case 'pass':
      return { color: 'green', label: 'Compliant', icon: 'check-circle' };
    case 'fail':
      return { color: 'red', label: 'Non-Compliant', icon: 'x-circle' };
    case 'needs_review':
      return { color: 'yellow', label: 'Needs Review', icon: 'alert-triangle' };
    default:
      return { color: 'gray', label: 'Unknown', icon: 'circle-help' };
  }
}

/**
 * Hook to get severity badge styling
 */
export function useSeverityStyle(severity?: 'critical' | 'warning' | 'info') {
  switch (severity) {
    case 'critical':
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        icon: 'alert-circle',
      };
    case 'warning':
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200',
        icon: 'alert-triangle',
      };
    case 'info':
      return {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        icon: 'info',
      };
    default:
      return {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        icon: 'circle',
      };
  }
}

/**
 * Check if a jurisdiction is in a High Velocity Hurricane Zone
 */
export function isHVHZ(jurisdiction?: Jurisdiction | null): boolean {
  return jurisdiction?.overlays?.highVelocityHurricaneZone === true;
}

/**
 * Check if a jurisdiction is in a coastal zone
 */
export function isCoastal(jurisdiction?: Jurisdiction | null): boolean {
  return jurisdiction?.overlays?.coastalZone === true;
}

/**
 * Check if a jurisdiction is in a flood zone
 */
export function isFloodZone(jurisdiction?: Jurisdiction | null): boolean {
  return jurisdiction?.overlays?.floodZone === true;
}
